import { Alert, Input, Layouts, Loader, TextArea, UploadImages } from 'components'
import s from './styles.module.scss'
import DashBoardNav from '../../dashboardNav'
import { useState, useEffect, useCallback, useRef } from 'react'
import { head, imageCompressor } from 'helpers'
import * as api from 'api'
import { useNavigate } from 'react-router-dom'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST


export default function Main() {
  const [loading, setLoading] = useState(false)
  const [testimonies, setTestimonies] = useState([])
  const [newTestimonies, setNewTestimonies] = useState([])
  const [testimoniesImages, setTestimoniesImages] = useState([])
  const [testimoniesImagesUrls, setTestimoniesImagesUrls] = useState([])
  const [newTestimoniesImages, setNewTestimoniesImages] = useState([])
  const [newTestimoniesImagesUrls, setNewTestimoniesImagesUrls] = useState([])
  const [testimoniesToDelete, setTestimoniesToDelete] = useState([])
  const navigate = useNavigate()
  const processing = useRef(false)

  const getTestimonies = useCallback(async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    try {
      const res = await api.admin.testimony.fetch({})
      
      if (res?.code === 200) {
        const fetchedTestimonies = res.payload.testimonies || []
        setTestimonies(fetchedTestimonies)
        setTestimoniesImages(Array(fetchedTestimonies.length).fill(null))
        // Handle both full URLs and filenames
        setTestimoniesImagesUrls(fetchedTestimonies.map(t => {
          if (!t.image) return null
          return IMAGE_HOST + t.image
        }))
      } else {
        Alert.error(res?.message || 'Failed to fetch testimonies')
      }
    } catch (error) {
      console.error('Error fetching testimonies:', error)
      Alert.error('Failed to load testimonies')
    }

    setLoading(false)
    processing.current = false
  }, [])

  useEffect(() => {
    getTestimonies()
  }, [getTestimonies])

  useEffect(() => {
    head({ title: 'Testimonies | BCN' })
  }, [])

  // Handlers for existing testimonies
  const handleTestimonyChange = (index, field, value) => {
    const updated = [...testimonies]
    updated[index] = { ...updated[index], [field]: value }
    setTestimonies(updated)
  }

  const handleTestimonyImageChange = async (e, index) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const compressedFiles = await imageCompressor([file], 'testimony')
      if (compressedFiles[0]) {
        const imageUrl = URL.createObjectURL(compressedFiles[0])
        const updatedImages = [...testimoniesImages]
        const updatedUrls = [...testimoniesImagesUrls]
        updatedImages[index] = compressedFiles[0]
        updatedUrls[index] = imageUrl
        setTestimoniesImages(updatedImages)
        setTestimoniesImagesUrls(updatedUrls)
      }
    } catch (error) {
      console.error('Error compressing image:', error)
      Alert.error('Failed to process image')
    }
  }

  const handleRemoveTestimony = (index) => {
    const testimony = testimonies[index]
    if (testimony._id) {
      setTestimoniesToDelete([...testimoniesToDelete, testimony._id])
    }
    
    const updated = testimonies.filter((_, i) => i !== index)
    const updatedImages = testimoniesImages.filter((_, i) => i !== index)
    const updatedUrls = testimoniesImagesUrls.filter((_, i) => i !== index)
    
    setTestimonies(updated)
    setTestimoniesImages(updatedImages)
    setTestimoniesImagesUrls(updatedUrls)
  }

  // Handlers for new testimonies
  const handleNewTestimonyChange = (index, field, value) => {
    const updated = [...newTestimonies]
    updated[index] = { ...updated[index], [field]: value }
    setNewTestimonies(updated)
  }

  const handleNewTestimonyImageChange = async (e, index) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const compressedFiles = await imageCompressor([file], 'testimony')
      if (compressedFiles[0]) {
        const imageUrl = URL.createObjectURL(compressedFiles[0])
        const updatedImages = [...newTestimoniesImages]
        const updatedUrls = [...newTestimoniesImagesUrls]
        updatedImages[index] = compressedFiles[0]
        updatedUrls[index] = imageUrl
        setNewTestimoniesImages(updatedImages)
        setNewTestimoniesImagesUrls(updatedUrls)
      }
    } catch (error) {
      console.error('Error compressing image:', error)
      Alert.error('Failed to process image')
    }
  }

  const handleRemoveNewTestimony = (index) => {
    const updated = newTestimonies.filter((_, i) => i !== index)
    const updatedImages = newTestimoniesImages.filter((_, i) => i !== index)
    const updatedUrls = newTestimoniesImagesUrls.filter((_, i) => i !== index)
    
    setNewTestimonies(updated)
    setNewTestimoniesImages(updatedImages)
    setNewTestimoniesImagesUrls(updatedUrls)
  }

  const addNewTestimony = () => {
    setNewTestimonies([...newTestimonies, { testimony: '', name: '', designation: '' }])
    setNewTestimoniesImages([...newTestimoniesImages, null])
    setNewTestimoniesImagesUrls([...newTestimoniesImagesUrls, null])
  }

  const saveHandler = async () => {
    // Validate existing testimonies
    for (const testimony of testimonies) {
      if (!testimony.testimony?.trim() || !testimony.name?.trim() || !testimony.designation?.trim()) {
        Alert.warn('Please fill all fields for existing testimonies')
        return
      }
    }

    // Validate new testimonies
    for (const testimony of newTestimonies) {
      if (!testimony.testimony?.trim() || !testimony.name?.trim() || !testimony.designation?.trim()) {
        Alert.warn('Please fill all fields for new testimonies')
        return
      }
    }

    if (processing.current) return
    processing.current = true
    Alert.success('Saving testimonies...')
    setLoading(true)

    try {
      // Delete removed testimonies
      for (const id of testimoniesToDelete) {
        await api.admin.testimony.delete(id)
      }

      // Update existing testimonies
      for (let i = 0; i < testimonies.length; i++) {
        const testimony = testimonies[i]
        const data = new FormData()
        data.append('testimony', testimony.testimony)
        data.append('name', testimony.name)
        data.append('designation', testimony.designation)
        
        if (testimoniesImages[i]) {
          data.append('image', testimoniesImages[i])
        }

        await api.admin.testimony.update(testimony._id, data)
      }

      // Create new testimonies
      for (let i = 0; i < newTestimonies.length; i++) {
        const testimony = newTestimonies[i]
        const data = new FormData()
        data.append('testimony', testimony.testimony)
        data.append('name', testimony.name)
        data.append('designation', testimony.designation)
        
        if (newTestimoniesImages[i]) {
          data.append('image', newTestimoniesImages[i])
        }

        await api.admin.testimony.create(data)
      }

      Alert.success('Testimonies saved successfully')
      navigate(-1)
    } catch (error) {
      console.error('Error saving testimonies:', error)
      Alert.error('Failed to save testimonies')
    }

    setLoading(false)
    processing.current = false
  }

  return (
    <div className={s.main}>
      <div className={s.testimoniesOuter}>
        <Layouts.Classic title='Success Stories'>
          <div className={s.headerBottom}>
            <div className={s.addButton} onClick={saveHandler}>
              <span className='material-icons-outlined'>save</span> Save
            </div>
          </div>
        </Layouts.Classic>

        {!loading ? (
          <div className={s.content + ' innerScrollX'}>
            <DashBoardNav />
            
            <div className={s.testimonies}>
              <div className={s.label}>Success Stories</div>
              
              {/* Existing Testimonies */}
              {testimonies.map((testimony, i) => (
                <TestimonyForm
                  key={`existing-${testimony._id || i}`}
                  index={i}
                  testimony={testimony}
                  imageUrl={testimoniesImagesUrls[i]}
                  onChange={handleTestimonyChange}
                  onImageChange={handleTestimonyImageChange}
                  onRemove={handleRemoveTestimony}
                  isNew={false}
                />
              ))}

              {/* New Testimonies */}
              {newTestimonies.map((testimony, i) => (
                <TestimonyForm
                  key={`new-${i}`}
                  index={i}
                  displayIndex={testimonies.length + i}
                  testimony={testimony}
                  imageUrl={newTestimoniesImagesUrls[i]}
                  onChange={handleNewTestimonyChange}
                  onImageChange={handleNewTestimonyImageChange}
                  onRemove={handleRemoveNewTestimony}
                  isNew={true}
                />
              ))}
            </div>

            <div className={s.addMore}>
              <div onClick={addNewTestimony}>
                Add Story <span className='material-icons-outlined'>add_circle_outline</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={s.loader}>
            <Loader message='Loading testimonies...' color='var(--c-primary)' colorText='var(--c-primary)' />
          </div>
        )}
      </div>
    </div>
  )
}

const TestimonyForm = ({ index, displayIndex, testimony, imageUrl, onChange, onImageChange, onRemove, isNew }) => {
  const actualIndex = displayIndex !== undefined ? displayIndex : index
  
  return (
    <div className={s.testimony}>
      <div className={s.title}>
        <span>Testimony {actualIndex + 1}</span>
        <span onClick={() => onRemove(index)}>
          Remove <span className='material-icons-outlined'>cancel</span>
        </span>
      </div>

      <div className={s.imageUpload}>
        <UploadImages.Single
          userLogoUrl={imageUrl || ''}
          handleUserLogoChange={(e) => onImageChange(e, index)}
          id={`testimonyImage-${isNew ? 'new' : 'existing'}-${index}`}
        />
      </div>

      <Input.Classic
        type='text'
        label='Name'
        iconLeft='person'
        placeholder='Enter person name'
        value={testimony.name || ''}
        onChange={(e) => onChange(index, 'name', e.target.value)}
      />

      <Input.Classic
        type='text'
        label='Designation'
        iconLeft='work'
        placeholder='Enter designation/title'
        value={testimony.designation || ''}
        onChange={(e) => onChange(index, 'designation', e.target.value)}
      />

      <TextArea.Classic
        label='Testimony'
        iconLeft='format_quote'
        placeholder='Enter testimony text...'
        value={testimony.testimony || ''}
        onChange={(e) => onChange(index, 'testimony', e.target.value)}
        rows={6}
      />
    </div>
  )
}
